// Lesson types — barn config for what a lesson can be.
//
// These were hard-coded in two components and a SQL comment. Taylor asked to
// name her own (jump height, flatwork, group, private) and to color-code the
// schedule; both come from here.
//
// Capacity is what makes a type a group: 1 is private, higher is a group, and
// it's what will later let a client see "2 of 4 booked".
//
// Retiring is deliberately not deleting. A type that has been used names a
// lesson in the past, so it stays readable on old lessons and just stops being
// offered on new ones.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { COLOR_TOKENS, colorClass, colorLabel } from '../../lib/lessonTypes';
import { IconButton, PencilIcon } from '../../components/ui/icons';

export default function LessonTypesPage() {
  const { activeRole } = useAuth();
  const canEdit = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState('');

  const typesQ = useQuery({
    queryKey: ['lesson-types', 'all'],
    queryFn: () => api.get('/lesson-types?all=1'),
    enabled: canEdit,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['lesson-types'] });
    qc.invalidateQueries({ queryKey: ['schedule'] });
    qc.invalidateQueries({ queryKey: ['lessons'] });
  };

  const save = useMutation({
    mutationFn: ({ id, ...fields }) =>
      id ? api.patch(`/lesson-types/${id}`, fields) : api.post('/lesson-types', fields),
    onSuccess: () => { setAdding(false); setEditingId(null); setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const retire = useMutation({
    mutationFn: (id) => api.del(`/lesson-types/${id}`),
    onSuccess: () => { setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const restore = useMutation({
    mutationFn: (id) => api.patch(`/lesson-types/${id}`, { active: true }),
    onSuccess: () => { setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  if (!canEdit) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">
          Lesson types are set by an owner or manager.
        </div>
      </div>
    );
  }

  const all = typesQ.data?.lessonTypes ?? [];
  const active = all.filter((t) => t.active);
  const retired = all.filter((t) => !t.active);

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24 sm:pb-8">
      <header className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Lesson Types</h1>
          <p className="text-sm text-ink-500">
            What a lesson can be, how long it runs, and its color on the schedule.
          </p>
        </div>
        {!adding && (
          <button onClick={() => { setAdding(true); setEditingId(null); }} className="btn-primary shrink-0">
            + Add Type
          </button>
        )}
      </header>

      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}

      {adding && (
        <TypeForm
          onCancel={() => setAdding(false)}
          onSave={(fields) => save.mutate(fields)}
          saving={save.isPending}
        />
      )}

      {typesQ.isLoading ? (
        <div className="card p-8 text-center text-ink-400">Loading…</div>
      ) : (
        <ul className="card divide-y divide-ink-100">
          {active.map((t) =>
            editingId === t.id ? (
              <li key={t.id} className="p-3">
                <TypeForm
                  initial={t}
                  onCancel={() => setEditingId(null)}
                  onSave={(fields) => save.mutate({ id: t.id, ...fields })}
                  saving={save.isPending}
                />
              </li>
            ) : (
              <TypeRow
                key={t.id}
                t={t}
                busy={save.isPending || retire.isPending}
                onEdit={() => { setAdding(false); setEditingId(t.id); }}
                onRetire={() => {
                  if (window.confirm(`Retire "${t.name}"? Past lessons keep it; it stops being offered on new ones.`)) {
                    retire.mutate(t.id);
                  }
                }}
              />
            ),
          )}
          {active.length === 0 && (
            <li className="p-6 text-center text-sm text-ink-400">No lesson types yet.</li>
          )}
        </ul>
      )}

      {retired.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-medium text-ink-500 mb-2">Retired</h2>
          <ul className="card divide-y divide-ink-100">
            {retired.map((t) => (
              <li key={t.id} className="p-3 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 min-w-0">
                  <span className={'w-2.5 h-2.5 rounded-full shrink-0 ' + colorClass(t.color, 'dot')} />
                  <span className="text-ink-500 truncate">{t.name}</span>
                </span>
                <button
                  onClick={() => restore.mutate(t.id)}
                  disabled={restore.isPending}
                  className="text-sm text-ink-600 hover:text-ink-900 underline shrink-0"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

const BOOKING_RULES = [
  { value: 'instant', label: 'Anyone eligible can book it', hint: 'They pick it and it is theirs.' },
  { value: 'approval', label: 'They ask, I say yes', hint: 'It waits as a request until you accept it.' },
  { value: 'trainer_only', label: 'Only I can fill it', hint: 'Clients never see it. You book into it yourself.' },
];

function TypeRow({ t, busy, onEdit, onRetire }) {
  return (
    <li className="p-3 flex flex-wrap items-center gap-3">
      <span className="flex items-center gap-2 min-w-0 flex-1">
        <span className={'w-3 h-3 rounded-full shrink-0 ' + colorClass(t.color, 'dot')} />
        <span className="min-w-0">
          <span className="font-medium text-ink-900 block truncate">{t.name}</span>
          <span className="text-sm text-ink-500">
            {t.defaultMinutes} min
            {t.defaultCapacity > 1
              ? ` · group of ${t.defaultCapacity}`
              : ' · private'}
            {t.defaultChangeoverMinutes > 0 && ` · ${t.defaultChangeoverMinutes} min changeover`}
            {t.bookingRule === 'approval' && ' · needs your OK'}
            {t.bookingRule === 'trainer_only' && ' · you book it'}
          </span>
        </span>
      </span>
      <span className="flex items-center gap-3 shrink-0">
        <IconButton icon={PencilIcon} label="Edit" onClick={onEdit} disabled={busy} />
        <button
          onClick={onRetire}
          disabled={busy}
          className="text-ink-300 hover:text-red-600 px-1"
          aria-label={`Retire ${t.name}`}
        >
          ✕
        </button>
      </span>
    </li>
  );
}

function TypeForm({ initial, onCancel, onSave, saving }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState(initial?.color ?? 'indigo');
  const [minutes, setMinutes] = useState(String(initial?.defaultMinutes ?? 45));
  const [capacity, setCapacity] = useState(String(initial?.defaultCapacity ?? 1));
  const [changeover, setChangeover] = useState(String(initial?.defaultChangeoverMinutes ?? 0));
  const [rule, setRule] = useState(initial?.bookingRule ?? 'instant');

  const canSave = name.trim() && !saving;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        onSave({
          name: name.trim(),
          color,
          defaultMinutes: Number(minutes) || 45,
          defaultCapacity: Number(capacity) || 1,
          // Zero is a real answer and the common one, so `||` would be wrong
          // here — back-to-back lessons must stay back to back.
          defaultChangeoverMinutes: Math.max(0, Number(changeover) || 0),
          bookingRule: rule,
        });
      }}
      className={initial ? 'space-y-3' : 'card p-4 mb-4 space-y-3'}
    >
      <div>
        <label htmlFor="lt-name" className="block text-sm font-medium text-ink-700 mb-1">Name</label>
        <input
          id="lt-name"
          className="input"
          placeholder="e.g. Jumping 2'6&quot;"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label htmlFor="lt-mins" className="block text-sm font-medium text-ink-700 mb-1">
            Length (minutes)
          </label>
          <input
            id="lt-mins"
            className="input"
            type="number"
            min="5"
            max="480"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="lt-cap" className="block text-sm font-medium text-ink-700 mb-1">
            Riders per lesson
          </label>
          <input
            id="lt-cap"
            className="input"
            type="number"
            min="1"
            max="20"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
          />
          <p className="text-xs text-ink-400 mt-1">
            {Number(capacity) > 1 ? 'A group lesson.' : 'A private lesson.'}
          </p>
        </div>
        <div>
          {/* The last of the four numbers that used to be typed by hand every
              time somebody opened availability. It belongs here for the same
              reason length does: how long you need before the next lesson is a
              property of teaching THIS kind of lesson, not of a Tuesday. */}
          <label htmlFor="lt-gap" className="block text-sm font-medium text-ink-700 mb-1">
            Changeover (minutes)
          </label>
          <input
            id="lt-gap"
            className="input"
            type="number"
            min="0"
            max="120"
            value={changeover}
            onChange={(e) => setChangeover(e.target.value)}
          />
          <p className="text-xs text-ink-400 mt-1">
            {Number(changeover) > 0
              ? `${Number(changeover)} min before the next one.`
              : 'Back to back.'}
          </p>
        </div>
      </div>

      <div>
        {/* The bottom of the chain. A block or a single slot can still say
            otherwise, but this is the answer when nobody has — and "jumping
            needs me to approve it" is a fact about jumping, not about
            Tuesday. */}
        <span className="block text-sm font-medium text-ink-700 mb-1">When a client picks this</span>
        <div className="space-y-1.5">
          {BOOKING_RULES.map((r) => (
            <label key={r.value} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="lt-rule"
                className="mt-1"
                checked={rule === r.value}
                onChange={() => setRule(r.value)}
              />
              <span>
                {r.label}
                <span className="block text-xs text-ink-400">{r.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <span className="block text-sm font-medium text-ink-700 mb-1">Color on the schedule</span>
        <div className="flex flex-wrap gap-2">
          {COLOR_TOKENS.map((tok) => (
            <button
              key={tok}
              type="button"
              onClick={() => setColor(tok)}
              aria-label={colorLabel(tok)}
              aria-pressed={color === tok}
              title={colorLabel(tok)}
              className={
                'w-8 h-8 rounded-full ' +
                colorClass(tok, 'dot') +
                (color === tok ? ' ring-2 ring-offset-2 ring-ink-800' : '')
              }
            />
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={!canSave} className="btn-primary">
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Type'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

function friendly(e) {
  switch (e?.body?.error || e?.message || '') {
    case 'name_taken':
      return 'A lesson type with that name already exists.';
    case 'missing_name':
      return 'Give the type a name.';
    case 'invalid_color':
      return 'Pick a color from the list.';
    case 'forbidden':
      return 'Only an owner or manager can change lesson types.';
    // Everything the switch above doesn't name. It used to land here too, as
    // "Try again", which was advice rather than information and was wrong as
    // often as not — retrying a 404 forever will not fix a 404. The code and
    // the status are the only things that distinguish one unknown failure
    // from another, so they are what gets said.
    default: {
      const code = e?.body?.error || e?.message;
      const status = e?.status;
      if (!code && !status) return 'No response from the server. Check your connection.';
      return `Couldn’t save that${code ? `: ${code}` : ''}${status ? ` (${status})` : ''}.`;
    }
  }
}
